import React, { useState, useEffect } from 'react';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n/I18nContext';
import { IntegrationService } from '@/services/IntegrationService';
import { clearBrewfatherCredentialsCache } from '@/services/BrewfatherService';
import { PaymentService } from '@/services/PaymentService';
import { UserService } from '@/services/UserService';
import { cognitoService } from '@/services/cognito';
import type { SubscriptionResponse } from '@/services/PaymentService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import SaveIcon from '@mui/icons-material/Save';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PublicIcon from '@mui/icons-material/Public';
import { useNavigate } from 'react-router-dom';
import Link from '@mui/material/Link';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import { COUNTRIES } from '@/utils/countries';

const DashboardPage: React.FC = () => {
	const { user, getAuthHeader, getUserId } = useAuth();
	const { t } = useI18n();
	const navigate = useNavigate();
	const [brewfatherApiKey, setBrewfatherApiKey] = useState('');
	const [brewfatherUserId, setBrewfatherUserId] = useState('');
	const [loading, setLoading] = useState(false);
	const [hasCredentials, setHasCredentials] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [deleting, setDeleting] = useState(false);
	// OpenAI integration (optional)
	const [openaiApiKey, setOpenaiApiKey] = useState('');
	const [openaiHasKey, setOpenaiHasKey] = useState(false);
	const [openaiEditing, setOpenaiEditing] = useState(false);
	const [openaiLoading, setOpenaiLoading] = useState(false);
	const [openaiDeleteDialogOpen, setOpenaiDeleteDialogOpen] = useState(false);
	const [openaiDeleting, setOpenaiDeleting] = useState(false);
	// Subscription (plan) for display and portal link
	const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
	const [subscriptionLoading, setSubscriptionLoading] = useState(false);
	const [portalLoading, setPortalLoading] = useState(false);
	// User profile editing
	const [isEditingProfile, setIsEditingProfile] = useState(false);
	const [profileName, setProfileName] = useState('');
	const [profileEmail, setProfileEmail] = useState('');
	const [profileCountry, setProfileCountry] = useState('');
	const [profileLoading, setProfileLoading] = useState(false);

	// Load user profile data
	useEffect(() => {
		if (user) {
			setProfileName(user.name || '');
			setProfileEmail(user.email || '');
			// Country is stored as locale in Cognito, accessible via user.country
			setProfileCountry((user as any).country || '');
		}
	}, [user]);

	useEffect(() => {
		const loadCredentials = async () => {
		if (!user) return;
		const userId = getUserId();
		if (!userId) return;
		try {
		const authHeader = getAuthHeader();
		const response = await IntegrationService.get(userId, 'brewfather', authHeader);
		if (response.item?.credentials) {
		const creds = response.item.credentials as { userId: string; apiKey: string };
		setBrewfatherApiKey(creds.apiKey);
		setBrewfatherUserId(creds.userId);
		setHasCredentials(true);
		setIsEditing(false);
	} else {
		setHasCredentials(false);
		setIsEditing(true);
	}
	} catch (error: any) {
	// Integration not found - that's ok, user can set it up
	if (!error.message?.includes('404') && !error.message?.includes('not found')) {
		console.error('Error loading credentials:', error);
	}
	setHasCredentials(false);
	setIsEditing(true);
	}
	};
	loadCredentials();
}, [user, getUserId, getAuthHeader]);

	// Load OpenAI integration
	useEffect(() => {
		const loadOpenAI = async () => {
			if (!user) return;
			const userId = getUserId();
			if (!userId) return;
			try {
				const authHeader = getAuthHeader();
				const response = await IntegrationService.get(userId, 'openai', authHeader);
				if (response.item?.credentials?.apiKey) {
					setOpenaiApiKey('');
					setOpenaiHasKey(true);
					setOpenaiEditing(false);
				} else {
					setOpenaiHasKey(false);
					setOpenaiEditing(true);
				}
			} catch {
				setOpenaiHasKey(false);
				setOpenaiEditing(true);
			}
		};
		loadOpenAI();
	}, [user, getUserId, getAuthHeader]);

	// Load current subscription for display
	useEffect(() => {
		if (!user) {
			setSubscription(null);
			return;
		}
		const userId = getUserId();
		const authHeader = getAuthHeader() as Record<string, string> | undefined;
		if (!userId || !authHeader?.Authorization) {
			setSubscription(null);
			return;
		}
		setSubscriptionLoading(true);
		PaymentService.getSubscription(userId, authHeader)
			.then((res) => setSubscription(res))
			.catch(() => setSubscription(null))
			.finally(() => setSubscriptionLoading(false));
	}, [user, getUserId, getAuthHeader]);

	const handleChangePlan = async () => {
		const userId = getUserId();
		const authHeader = getAuthHeader() as Record<string, string> | undefined;
		if (!userId || !authHeader?.Authorization) return;
		setPortalLoading(true);
		try {
			const { url } = await PaymentService.getPortalUrl(userId, authHeader);
			if (url) window.location.href = url;
		} catch (e: any) {
			toast.error(e?.message || 'Could not open subscription management.');
		} finally {
			setPortalLoading(false);
		}
	};

	const handleSaveBrewfather = async () => {
		if (!brewfatherApiKey || !brewfatherUserId) {
		toast.error('Please enter both User ID and API Key');
		return;
	}
		if (!user) {
		toast.error('Please log in to save credentials');
		return;
	}
	try {
		setLoading(true);
		const userId = getUserId();
		if (!userId) {
			throw new Error('User ID not available');
		}
		const authHeader = getAuthHeader();
		await IntegrationService.createOrUpdate(
		userId,
		'brewfather',
	{
		userId: brewfatherUserId,
		apiKey: brewfatherApiKey
	},
	authHeader
	);
		clearBrewfatherCredentialsCache();
		setHasCredentials(true);
		setIsEditing(false);
		toast.success(t.dashboard.credentialsSaved);
	} catch (error: any) {
		console.error('Error saving credentials:', error);
		toast.error(error.message || 'Failed to save credentials');
	} finally {
		setLoading(false);
	}
	};

	const handleDeleteBrewfather = async () => {
		if (!user) {
		toast.error('Please log in to delete credentials');
		return;
	}
		try {
		setDeleting(true);
		const userId = getUserId();
		if (!userId) {
			throw new Error('User ID not available');
		}
		const authHeader = getAuthHeader();
	await IntegrationService.delete(userId, 'brewfather', authHeader);
		clearBrewfatherCredentialsCache();
		setHasCredentials(false);
		setIsEditing(true);
		setBrewfatherApiKey('');
		setBrewfatherUserId('');
		setDeleteDialogOpen(false);
		toast.success('Brewfather credentials deleted successfully');
	} catch (error: any) {
		console.error('Error deleting credentials:', error);
		toast.error(error.message || 'Failed to delete credentials');
	} finally {
		setDeleting(false);
	}
	};

	const handleEdit = () => {
		setIsEditing(true);
	};

	const handleSaveOpenai = async () => {
		if (!openaiApiKey.trim()) {
			toast.error('Please enter your OpenAI API key');
			return;
		}
		if (!user) {
			toast.error('Please log in to save');
			return;
		}
		try {
			setOpenaiLoading(true);
			const userId = getUserId();
			if (!userId) throw new Error('User ID not available');
			const authHeader = getAuthHeader();
			await IntegrationService.createOrUpdate(userId, 'openai', { apiKey: openaiApiKey.trim() }, authHeader);
			setOpenaiHasKey(true);
			setOpenaiEditing(false);
			setOpenaiApiKey('');
			toast.success(t.dashboard.openaiSaved);
		} catch (error: any) {
			console.error('Error saving OpenAI key:', error);
			toast.error(error.message || 'Failed to save OpenAI API key');
		} finally {
			setOpenaiLoading(false);
		}
	};

	const handleDeleteOpenai = async () => {
		if (!user) return;
		try {
			setOpenaiDeleting(true);
			const userId = getUserId();
			if (!userId) throw new Error('User ID not available');
			const authHeader = getAuthHeader();
			await IntegrationService.delete(userId, 'openai', authHeader);
			setOpenaiHasKey(false);
			setOpenaiEditing(true);
			setOpenaiApiKey('');
			setOpenaiDeleteDialogOpen(false);
			toast.success(t.dashboard.openaiDeleted);
		} catch (error: any) {
			console.error('Error deleting OpenAI key:', error);
			toast.error(error.message || 'Failed to remove OpenAI API key');
		} finally {
			setOpenaiDeleting(false);
		}
	};

	const handleCancelEdit = () => {
		// Reload credentials to restore original values
		const loadCredentials = async () => {
		if (!user) return;
		const userId = getUserId();
		if (!userId) return;
		try {
		const authHeader = getAuthHeader();
		const response = await IntegrationService.get(userId, 'brewfather', authHeader);
		if (response.item?.credentials) {
		const creds = response.item.credentials as { userId: string; apiKey: string };
		setBrewfatherApiKey(creds.apiKey);
		setBrewfatherUserId(creds.userId);
		setIsEditing(false);
	} else {
		setBrewfatherApiKey('');
		setBrewfatherUserId('');
		setIsEditing(true);
	}
	} catch (error: any) {
		setBrewfatherApiKey('');
		setBrewfatherUserId('');
		setIsEditing(true);
	}
	};
	loadCredentials();
	};

	const handleEditProfile = () => {
		setIsEditingProfile(true);
	};

	const handleCancelProfileEdit = () => {
		setIsEditingProfile(false);
		if (user) {
			setProfileName(user.name || '');
			setProfileEmail(user.email || '');
			setProfileCountry((user as any).country || '');
		}
	};

	const handleSaveProfile = async () => {
		if (!user) return;
		if (!profileName.trim() || !profileEmail.trim()) {
			toast.error('Name and email are required');
			return;
		}
		try {
			setProfileLoading(true);
			const accessToken = localStorage.getItem('cognito.accessToken');
			if (!accessToken) {
				throw new Error('Not authenticated');
			}

			// Update Cognito attributes (locale for country, name, email)
			const attributes: Array<{ Name: string; Value: string }> = [
				{ Name: 'name', Value: profileName.trim() },
				{ Name: 'email', Value: profileEmail.trim() }
			];
			
			// Store country as locale in Cognito
			if (profileCountry) {
				attributes.push({ Name: 'locale', Value: profileCountry });
			}

			await cognitoService.updateUserAttributes(accessToken, attributes);

			// Also update backend user record (for name/email if needed)
			const authHeader = getAuthHeader();
			const updates: any = {
				name: profileName.trim(),
				email: profileEmail.trim()
			};
			try {
				await UserService.update(updates, authHeader);
			} catch (backendError) {
				// Backend update is optional - Cognito is the source of truth
				console.warn('Backend update failed, but Cognito update succeeded:', backendError);
			}

			toast.success('Profile updated successfully');
			setIsEditingProfile(false);
			// Reload the page to refresh user data and get new tokens
			window.location.reload();
		} catch (error: any) {
			console.error('Error updating profile:', error);
			toast.error(error.message || 'Failed to update profile');
		} finally {
			setProfileLoading(false);
		}
	};

	return (
		<Container maxWidth="md" sx={{ py: { xs: 2, md: 4 }, px: { xs: 2, md: 3 } }}>
			<ToastContainer position="top-right" />
			<Typography variant="h4" fontWeight={800} sx={{ mb: { xs: 3, md: 4 }, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
				{t.dashboard.title}
			</Typography>

			<Grid container spacing={{ xs: 2, md: 3 }}>
				<Grid item xs={12}>
					<Paper sx={{ p: { xs: 2, md: 3 } }}>
						<Stack 
							direction={{ xs: 'column', sm: 'row' }} 
							spacing={2} 
							alignItems={{ xs: 'flex-start', sm: 'center' }} 
							justifyContent="space-between" 
							sx={{ mb: 2 }}
						>
							<Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
								{t.dashboard.userInfo}
							</Typography>
							{!isEditingProfile && (
								<Button
									variant="outlined"
									size="small"
									startIcon={<EditIcon />}
									onClick={handleEditProfile}
									sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
								>
									{t.common.edit}
								</Button>
							)}
						</Stack>
						{isEditingProfile ? (
							<Stack spacing={2}>
								<TextField
									label={t.auth.name}
									value={profileName}
									onChange={(e) => setProfileName(e.target.value)}
									fullWidth
									required
									disabled={profileLoading}
									InputProps={{
										startAdornment: (
											<Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
												<PersonIcon color="action" />
											</Box>
										)
									}}
								/>
								<TextField
									type="email"
									label={t.auth.email}
									value={profileEmail}
									onChange={(e) => setProfileEmail(e.target.value)}
									fullWidth
									required
									disabled={profileLoading}
									InputProps={{
										startAdornment: (
											<Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
												<EmailIcon color="action" />
											</Box>
										)
									}}
								/>
								<TextField
									select
									label={t.auth.country}
									value={profileCountry}
									onChange={(e) => setProfileCountry(e.target.value)}
									fullWidth
									disabled={profileLoading}
									InputProps={{
										startAdornment: (
											<Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
												<PublicIcon color="action" />
											</Box>
										)
									}}
								>
									<MenuItem value="">
										<em>{t.auth.selectCountry}</em>
									</MenuItem>
									{COUNTRIES.map((c) => (
										<MenuItem key={c.code} value={c.code}>
											{c.name}
										</MenuItem>
									))}
								</TextField>
								<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
									<Button
										variant="contained"
										startIcon={<SaveIcon />}
										onClick={handleSaveProfile}
										disabled={profileLoading || !profileName.trim() || !profileEmail.trim()}
										fullWidth
									>
										{profileLoading ? t.common.loading : t.common.save}
									</Button>
									<Button
										variant="outlined"
										startIcon={<CancelIcon />}
										onClick={handleCancelProfileEdit}
										disabled={profileLoading}
										fullWidth
									>
										{t.common.cancel}
									</Button>
								</Stack>
							</Stack>
						) : (
							<Stack spacing={2}>
								<TextField
									label={t.auth.name}
									value={user?.name || ''}
									disabled
									fullWidth
									InputProps={{
										startAdornment: (
											<Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
												<PersonIcon color="action" />
											</Box>
										)
									}}
								/>
								<TextField
									label={t.auth.email}
									value={user?.email || ''}
									disabled
									fullWidth
									InputProps={{
										startAdornment: (
											<Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
												<EmailIcon color="action" />
											</Box>
										)
									}}
								/>
								{(user as any)?.country && (
									<TextField
										label={t.auth.country}
										value={COUNTRIES.find(c => c.code === (user as any).country)?.name || (user as any).country}
										disabled
										fullWidth
										InputProps={{
											startAdornment: (
												<Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
													<PublicIcon color="action" />
												</Box>
											)
										}}
									/>
								)}
							</Stack>
						)}
					</Paper>
				</Grid>

			<Grid item xs={12}>
				<Paper
					sx={{
						p: { xs: 2, md: 3 },
						background: (theme) =>
							theme.palette.mode === 'dark'
								? 'linear-gradient(135deg, rgba(255, 152, 0, 0.08) 0%, rgba(255, 152, 0, 0.03) 100%)'
								: 'linear-gradient(135deg, rgba(255, 152, 0, 0.05) 0%, rgba(255, 152, 0, 0.02) 100%)',
						border: (theme) =>
							`1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.25)' : 'rgba(255, 152, 0, 0.2)'}`
					}}
				>
					<Typography variant="h6" fontWeight={700} sx={{ mb: 2, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
						{t.dashboard.subscription}
					</Typography>
					<Stack 
						direction={{ xs: 'column', sm: 'row' }} 
						alignItems={{ xs: 'flex-start', sm: 'center' }} 
						justifyContent="space-between" 
						flexWrap="wrap" 
						gap={2}
					>
						{subscriptionLoading ? (
							<Stack direction="row" spacing={2} alignItems="center">
								<CircularProgress size={24} />
								<Typography color="text.secondary">{t.common.loading}</Typography>
							</Stack>
						) : (
							<>
								<Typography variant="body1" color="text.secondary">
									{t.dashboard.subscriptionPlan}:{' '}
									<Typography component="span" fontWeight={600} color="text.primary">
										{subscription?.hasSubscription
											? (subscription.planName || 'Subscription')
											: t.dashboard.freePlan}
									</Typography>
								</Typography>
								{subscription?.hasSubscription && (
									<Button
										variant="outlined"
										onClick={handleChangePlan}
										disabled={portalLoading}
										size="small"
									>
										{portalLoading ? <CircularProgress size={18} /> : t.dashboard.changePlan}
									</Button>
								)}
							</>
						)}
					</Stack>
				</Paper>
			</Grid>

			<Grid item xs={12}>
				<Paper
					sx={{
						p: { xs: 2, md: 3 },
						background: (theme) =>
							theme.palette.mode === 'dark'
								? 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.05) 100%)'
								: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.02) 100%)',
						border: (theme) =>
							`1px solid ${theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.3)' : 'rgba(25, 118, 210, 0.2)'}`
					}}
				>
					<Stack 
						direction={{ xs: 'column', md: 'row' }} 
						spacing={2} 
						alignItems={{ xs: 'flex-start', md: 'center' }} 
						justifyContent="space-between" 
						sx={{ mb: 3 }}
					>
						<Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
							<Box
								sx={{
									p: { xs: 1, md: 1.5 },
									borderRadius: 2,
									bgcolor: 'primary.main',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									flexShrink: 0
								}}
							>
								<VpnKeyIcon sx={{ color: 'primary.contrastText', fontSize: { xs: 24, md: 28 } }} />
							</Box>
							<Box sx={{ minWidth: 0, flex: 1 }}>
								<Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
									{t.dashboard.brewfatherIntegration}
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
									Connect your Brewfather account to sync batches and recipes
								</Typography>
							</Box>
						</Stack>
						<Link
							component="button"
							variant="body2"
							onClick={() => navigate('/brewfather-setup')}
							sx={{ 
								display: 'flex', 
								alignItems: 'center', 
								gap: 0.5, 
								textDecoration: 'none',
								alignSelf: { xs: 'flex-start', md: 'center' },
								fontSize: { xs: '0.8rem', md: '0.875rem' }
							}}
						>
							<HelpOutlineIcon fontSize="small" />
							{t.dashboard.setupGuide}
						</Link>
					</Stack>

					{hasCredentials && !isEditing ? (
						<>
							<Box
								sx={{
									p: { xs: 2, md: 2.5 },
									borderRadius: 2,
									bgcolor: 'background.default',
									border: '1px solid',
									borderColor: 'divider',
									display: 'flex',
									flexDirection: { xs: 'column', sm: 'row' },
									alignItems: { xs: 'flex-start', sm: 'center' },
									justifyContent: 'space-between',
									gap: 2,
									mb: 2
								}}
							>
								<Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
									<CheckCircleIcon sx={{ color: 'success.main', fontSize: { xs: 24, md: 28 }, flexShrink: 0 }} />
									<Box sx={{ minWidth: 0, flex: 1 }}>
										<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
											Brewfather User ID
										</Typography>
										<Typography 
											variant="h6" 
											fontWeight={600}
											sx={{ 
												fontSize: { xs: '1rem', md: '1.25rem' },
												wordBreak: 'break-all'
											}}
										>
											{brewfatherUserId}
										</Typography>
									</Box>
								</Stack>
								<Chip
									label="Connected"
									color="success"
									size="small"
									icon={<CheckCircleIcon />}
									sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
								/>
							</Box>
							<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
								<Button
									variant="outlined"
									startIcon={<EditIcon />}
									onClick={handleEdit}
									fullWidth
								>
									Edit Credentials
								</Button>
								<Button
									variant="outlined"
									color="error"
									startIcon={<DeleteIcon />}
									onClick={() => setDeleteDialogOpen(true)}
									fullWidth
								>
									Delete
								</Button>
							</Stack>
						</>
					) : (
						<>
							<Divider sx={{ mb: 2 }} />
							<Stack spacing={2}>
								<TextField
									label={t.dashboard.brewfatherUserId}
									value={brewfatherUserId}
									onChange={(e) => setBrewfatherUserId(e.target.value)}
									placeholder={t.dashboard.brewfatherUserId}
									fullWidth
									helperText={t.dashboard.brewfatherUserIdHelper}
								/>
								<TextField
									label={t.dashboard.brewfatherApiKey}
									type="password"
									value={brewfatherApiKey}
									onChange={(e) => setBrewfatherApiKey(e.target.value)}
									placeholder={t.dashboard.brewfatherApiKey}
									fullWidth
									helperText={t.dashboard.brewfatherApiKeyHelper}
								/>
								<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
									<Button
										variant="contained"
										startIcon={<SaveIcon />}
										onClick={handleSaveBrewfather}
										disabled={loading || !brewfatherApiKey || !brewfatherUserId}
										fullWidth
									>
										{loading ? t.common.loading : hasCredentials ? 'Update Credentials' : t.dashboard.saveCredentials}
									</Button>
									{hasCredentials && (
										<Button
											variant="outlined"
											onClick={handleCancelEdit}
											disabled={loading}
											fullWidth
										>
											Cancel
										</Button>
									)}
								</Stack>
							</Stack>
						</>
					)}
				</Paper>
			</Grid>

			<Grid item xs={12}>
				<Paper
					sx={{
						p: { xs: 2, md: 3 },
						background: (theme) =>
							theme.palette.mode === 'dark'
								? 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(16, 185, 129, 0.03) 100%)'
								: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%)',
						border: (theme) =>
							`1px solid ${theme.palette.mode === 'dark' ? 'rgba(16, 185, 129, 0.25)' : 'rgba(16, 185, 129, 0.2)'}`
					}}
				>
					<Stack 
						direction={{ xs: 'column', md: 'row' }} 
						spacing={2} 
						alignItems={{ xs: 'flex-start', md: 'center' }} 
						sx={{ mb: 2 }}
					>
						<Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
							<Box
								sx={{
									p: { xs: 1, md: 1.5 },
									borderRadius: 2,
									bgcolor: 'success.main',
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									flexShrink: 0
								}}
							>
								<VpnKeyIcon sx={{ color: 'success.contrastText', fontSize: { xs: 24, md: 28 } }} />
							</Box>
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
									{t.dashboard.openaiIntegration}
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
									{t.dashboard.openaiDescription}
								</Typography>
							</Box>
						</Stack>
						<Button
							variant="outlined"
							size="small"
							startIcon={<HelpOutlineIcon />}
							onClick={() => navigate('/openai-setup')}
							sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
						>
							{t.dashboard.setupGuide}
						</Button>
					</Stack>

					{openaiHasKey && !openaiEditing ? (
						<>
							<Box
								sx={{
									p: { xs: 2, md: 2.5 },
									borderRadius: 2,
									bgcolor: 'background.default',
									border: '1px solid',
									borderColor: 'divider',
									display: 'flex',
									flexDirection: { xs: 'column', sm: 'row' },
									alignItems: { xs: 'flex-start', sm: 'center' },
									justifyContent: 'space-between',
									gap: 2,
									mb: 2
								}}
							>
								<Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
									<CheckCircleIcon sx={{ color: 'success.main', fontSize: { xs: 24, md: 28 }, flexShrink: 0 }} />
									<Box sx={{ minWidth: 0, flex: 1 }}>
										<Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: { xs: '0.75rem', md: '0.875rem' } }}>
											{t.dashboard.openaiApiKey}
										</Typography>
										<Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>
											••••••••••••••••
										</Typography>
									</Box>
								</Stack>
								<Chip 
									label="Using your key" 
									color="success" 
									size="small" 
									icon={<CheckCircleIcon />}
									sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
								/>
							</Box>
							<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
								<Button
									variant="outlined"
									startIcon={<EditIcon />}
									onClick={() => setOpenaiEditing(true)}
									fullWidth
								>
									{t.common.edit}
								</Button>
								<Button
									variant="outlined"
									color="error"
									startIcon={<DeleteIcon />}
									onClick={() => setOpenaiDeleteDialogOpen(true)}
									fullWidth
								>
									{t.common.delete}
								</Button>
							</Stack>
						</>
					) : (
						<>
							<Divider sx={{ mb: 2 }} />
							<Stack spacing={2}>
								<TextField
									label={t.dashboard.openaiApiKey}
									type="password"
									value={openaiApiKey}
									onChange={(e) => setOpenaiApiKey(e.target.value)}
									placeholder="sk-..."
									fullWidth
									helperText={t.dashboard.openaiApiKeyHelper}
								/>
								<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
									<Button
										variant="contained"
										color="success"
										startIcon={<SaveIcon />}
										onClick={handleSaveOpenai}
										disabled={openaiLoading || !openaiApiKey.trim()}
										fullWidth
									>
										{openaiLoading ? t.common.loading : t.dashboard.saveCredentials}
									</Button>
									{openaiHasKey && (
										<Button
											variant="outlined"
											onClick={() => setOpenaiEditing(false)}
											disabled={openaiLoading}
											fullWidth
										>
											{t.common.cancel}
										</Button>
									)}
								</Stack>
							</Stack>
						</>
					)}
				</Paper>
			</Grid>

			<Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
				<DialogTitle>Delete Brewfather Credentials?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Are you sure you want to delete your Brewfather integration? This will remove your
						credentials and you'll need to reconnect to sync batches and recipes.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
						Cancel
					</Button>
					<Button
						onClick={handleDeleteBrewfather}
						color="error"
						variant="contained"
						disabled={deleting}
						startIcon={<DeleteIcon />}
					>
						{deleting ? 'Deleting...' : 'Delete'}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={openaiDeleteDialogOpen} onClose={() => !openaiDeleting && setOpenaiDeleteDialogOpen(false)}>
				<DialogTitle>Remove OpenAI API Key?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						AI features will use the default key again. You can add your own key anytime.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenaiDeleteDialogOpen(false)} disabled={openaiDeleting}>
						{t.common.cancel}
					</Button>
					<Button
						onClick={handleDeleteOpenai}
						color="error"
						variant="contained"
						disabled={openaiDeleting}
						startIcon={<DeleteIcon />}
					>
						{openaiDeleting ? t.common.loading : t.common.delete}
					</Button>
				</DialogActions>
			</Dialog>
			</Grid>
		</Container>
	);
};

export default DashboardPage;
